import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

const steps = [
  {
    id: 1,
    title: "Personalized Assessment",
    description:
      "We begin with a comprehensive assessment of your current language skills, learning style, and specific goals to create a tailored learning path.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
    detailedDescription:
      "Our assessment process begins with a comprehensive evaluation of your current language proficiency. We use a combination of standardized tests and conversational interviews to determine your starting level. Beyond just measuring vocabulary and grammar, we assess your learning style preferences, specific goals, and any challenges you've faced in previous language learning attempts. This allows us to create a truly personalized curriculum that aligns with how you learn best and focuses on the specific language skills most relevant to your goals, whether that's business communication, travel, academic study, or cultural immersion.",
    benefits: [
      "Customized learning path based on your current level",
      "Identification of your unique learning style",
      "Goal-oriented curriculum development",
      "Regular progress assessments to adjust your learning path",
    ],
  },
  {
    id: 2,
    title: "Immersive Learning",
    description:
      "Our methodology focuses on immersion through real-world conversations, cultural context, and practical scenarios rather than rote memorization.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
        />
      </svg>
    ),
    detailedDescription:
      "Traditional language learning often relies on memorization of vocabulary lists and grammar rules, which rarely translates to real-world fluency. Our immersive approach instead surrounds you with authentic language in context. From day one, you'll engage with the language through practical scenarios that mirror real-life situations. Our virtual environments simulate everyday interactions like ordering at restaurants, navigating public transportation, or conducting business meetings. Cultural context is woven throughout, ensuring you understand not just what to say, but when and how to say it appropriately within the cultural norms of native speakers.",
    benefits: [
      "Natural language acquisition through context",
      "Cultural insights integrated with language learning",
      "Practical scenarios relevant to your specific needs",
      "Faster path to conversational fluency",
    ],
  },
  {
    id: 3,
    title: "Expert Guidance",
    description:
      "Learn from certified native-speaking instructors who provide personalized feedback, cultural insights, and conversation practice in every session.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
    detailedDescription:
      "At the heart of our methodology are our instructors – all native speakers who are certified language teachers with extensive experience. Unlike automated apps or non-native teachers, our instructors provide nuanced feedback on pronunciation, intonation, and cultural appropriateness that only a native speaker can offer. They adapt their teaching style to match your learning preferences and pace, knowing when to challenge you and when to provide additional support. Beyond language instruction, they serve as cultural ambassadors, sharing insights about customs, traditions, and social norms that are essential for true fluency and cultural competence.",
    benefits: [
      "Native-speaking certified instructors",
      "Personalized feedback on pronunciation and intonation",
      "Cultural context from those who live the language",
      "Adaptive teaching styles to match your learning needs",
    ],
  },
  {
    id: 4,
    title: "Adaptive Technology",
    description:
      "Our platform adapts to your progress, automatically adjusting difficulty and focusing on areas where you need more practice for efficient learning.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
    detailedDescription:
      "Our proprietary learning platform uses artificial intelligence to continuously analyze your performance and learning patterns. The system identifies which concepts you've mastered and which require additional practice, then automatically adjusts your learning materials to focus on areas needing improvement. This ensures you never waste time reviewing content you've already mastered or move too quickly past concepts you haven't fully grasped. The platform also recognizes your peak learning times and preferred learning formats, suggesting optimal study schedules and content types to maximize retention and engagement.",
    benefits: [
      "AI-powered learning path that adapts to your progress",
      "Focused practice on areas needing improvement",
      "Spaced repetition system for optimal retention",
      "Data-driven insights about your learning patterns",
    ],
  },
];

const MethodologySection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);

  const openModal = () => {
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const selectStep = (step) => {
    setSelectedStep(step);
  };

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
      id="methodology"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full translate-x-1/3 translate-y-1/3"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our <span className="text-primary">Methodology</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our proven 4-step approach combines personalized instruction,
            immersive practice, and adaptive technology to help you achieve
            fluency faster.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="bg-white rounded-xl shadow-md overflow-hidden flex"
            >
              <div className="bg-primary/10 p-6 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-primary shadow-sm">
                  {step.icon}
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold mr-3">
                    {step.id}
                  </div>
                  <h3 className="font-bold text-xl text-gray-800">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <button
            onClick={openModal}
            className="bg-primary hover:bg-primary-dark text-white font-medium py-3 px-8 rounded-full transition duration-300 shadow-md hover:shadow-lg"
          >
            Learn More About Our Approach
          </button>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-full">
                {/* Sidebar */}
                <div className="w-64 bg-gray-50 border-r border-gray-100 p-6 hidden md:block">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">
                    Our Methodology
                  </h3>
                  <ul className="space-y-2">
                    {steps.map((step) => (
                      <li key={step.id}>
                        <button
                          onClick={() => selectStep(step)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 flex items-center ${
                            selectedStep?.id === step.id
                              ? "bg-primary text-white"
                              : "hover:bg-gray-100 text-gray-700"
                          }`}
                        >
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${
                              selectedStep?.id === step.id
                                ? "bg-white text-primary"
                                : "bg-primary/10 text-primary"
                            }`}
                          >
                            {step.id}
                          </div>
                          <span className="font-medium">{step.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => {
                        closeModal();
                        setTimeout(() => {
                          const element =
                            document.getElementById("contact-section");
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                          }
                        }, 300);
                      }}
                      className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition duration-300"
                    >
                      Start Learning
                    </button>
                  </div>
                </div>

                {/* Main content */}
                <div className="flex-1 flex flex-col max-h-[90vh] overflow-hidden">
                  {/* Header with mobile navigation */}
                  <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {selectedStep
                        ? selectedStep.title
                        : "Our Approach to Language Learning"}
                    </h2>
                    <button
                      onClick={closeModal}
                      className="text-gray-500 hover:text-gray-700 p-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Mobile tabs */}
                  <div className="md:hidden overflow-x-auto whitespace-nowrap p-4 border-b border-gray-100">
                    <div className="inline-flex space-x-2">
                      {steps.map((step) => (
                        <button
                          key={step.id}
                          onClick={() => selectStep(step)}
                          className={`px-4 py-2 rounded-full text-sm font-medium ${
                            selectedStep?.id === step.id
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {step.title}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Content area */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {selectedStep ? (
                      <div>
                        <div className="flex items-center mb-6">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-4">
                            {selectedStep.icon}
                          </div>
                          <h3 className="text-2xl font-bold text-gray-800">
                            {selectedStep.title}
                          </h3>
                        </div>

                        <p className="text-gray-700 mb-8 leading-relaxed">
                          {selectedStep.detailedDescription}
                        </p>

                        <h4 className="text-lg font-semibold text-gray-800 mb-4">
                          Key Benefits
                        </h4>
                        <ul className="space-y-3 mb-8">
                          {selectedStep.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-start">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-primary mt-1 mr-3 flex-shrink-0"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>

                        {/* Example or case study */}
                        <div className="bg-gray-50 rounded-xl p-6 mb-6">
                          <h4 className="text-lg font-semibold text-gray-800 mb-3">
                            How It Works In Practice
                          </h4>
                          <p className="text-gray-700 italic">
                            "Our {selectedStep.title.toLowerCase()} approach
                            helped Sarah progress from beginner to
                            conversational Spanish in just 4 months. By focusing
                            on her specific learning style and career-related
                            goals, we created a customized path that made every
                            minute of practice count."
                          </p>
                          <div className="mt-4 flex items-center">
                            <img
                              src="https://randomuser.me/api/portraits/women/44.jpg"
                              alt="Instructor"
                              className="w-10 h-10 rounded-full mr-3"
                            />
                            <div>
                              <p className="font-medium text-gray-800">
                                Maria Rodriguez
                              </p>
                              <p className="text-sm text-gray-500">
                                Senior Language Instructor
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-700 mb-8 leading-relaxed">
                          At Language Village, we've developed a comprehensive
                          methodology that combines the best practices in
                          language acquisition with innovative technology and
                          expert human instruction. Our approach is designed to
                          help you achieve fluency faster and more effectively
                          than traditional methods.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                          {steps.map((step) => (
                            <button
                              key={step.id}
                              onClick={() => selectStep(step)}
                              className="bg-white border border-gray-200 rounded-xl p-6 text-left hover:shadow-md transition-shadow duration-300"
                            >
                              <div className="flex items-center mb-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-3">
                                  {step.icon}
                                </div>
                                <h3 className="font-bold text-lg text-gray-800">
                                  {step.title}
                                </h3>
                              </div>
                              <p className="text-gray-600 text-sm">
                                {step.description}
                              </p>
                            </button>
                          ))}
                        </div>

                        <div className="bg-primary/5 rounded-xl p-6">
                          <h3 className="font-bold text-xl text-gray-800 mb-4">
                            Why Our Approach Works
                          </h3>
                          <p className="text-gray-700 mb-4">
                            Our methodology is based on decades of research in
                            language acquisition and cognitive science. By
                            combining personalized instruction, immersive
                            practice, expert guidance, and adaptive technology,
                            we create a learning environment that accelerates
                            fluency while making the process enjoyable and
                            engaging.
                          </p>
                          <p className="text-gray-700">
                            Select any of our methodology pillars above to learn
                            more about how each component contributes to your
                            language learning success.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer with CTA */}
                  <div className="p-6 border-t border-gray-100 bg-gray-50 md:hidden">
                    <button
                      onClick={() => {
                        closeModal();
                        setTimeout(() => {
                          const element =
                            document.getElementById("contact-section");
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                          }
                        }, 300);
                      }}
                      className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-lg transition duration-300"
                    >
                      Start Your Language Journey
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default MethodologySection;
